import { MONITORING_BID, MONITORING_CONTEXT } from './constants';
import {
  MonitoringCategories,
  MonitoringMetrics,
  sanitizeCategories,
  sanitizeMetrics
} from './sanitize';

type SlardarClient = (command: string, ...args: any[]) => void;

let initializationPromise: Promise<void> | undefined;

const getClient = (): SlardarClient | undefined => {
  if (typeof window === 'undefined' || typeof window.Slardar !== 'function') {
    return undefined;
  }
  return window.Slardar as SlardarClient;
};

export const initializeMonitoring = (): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (!initializationPromise) {
    initializationPromise = import('@oec-seller/sdk/utils/slardar')
      .then(({ slardarInit }) => {
        const clientBeforeInit = getClient();
        if (!clientBeforeInit) {
          return;
        }

        try {
          slardarInit({ bid: MONITORING_BID });
          const client = getClient();
          if (!client) {
            return;
          }
          client('context.merge', MONITORING_CONTEXT);
          client('start');
          // eslint-disable-next-line @i18n-ecom/no-meaningless/try-catch
        } catch (_error) {
          // Monitoring is fail-open and must never block application startup.
        }
      })
      .catch(() => {
        // Dynamic SDK loading is fail-open.
      });
  }

  return initializationPromise;
};

export const reportMonitoringEvent = (
  name: string,
  categories: Record<string, unknown>,
  metrics: Record<string, unknown> = {}
): void => {
  const client = getClient();
  if (!client) {
    return;
  }

  const safeCategories: MonitoringCategories = sanitizeCategories(categories);
  const safeMetrics: MonitoringMetrics = sanitizeMetrics(metrics);

  try {
    client('sendEvent', {
      name,
      categories: safeCategories,
      metrics: safeMetrics
    });
    // eslint-disable-next-line @i18n-ecom/no-meaningless/try-catch
  } catch (_error) {
    // Reporting is fail-open and is intentionally excluded from business promises.
  }
};

export const captureMonitoringException = (
  error: unknown,
  extra: Record<string, unknown> = {}
): void => {
  const client = getClient();
  if (!client) {
    return;
  }

  const normalizedError = error instanceof Error ? error : new Error('NonError exception');

  try {
    client('captureException', normalizedError, sanitizeCategories(extra));
    // eslint-disable-next-line @i18n-ecom/no-meaningless/try-catch
  } catch (_error) {
    // Reporting is fail-open and is intentionally excluded from business promises.
  }
};
